import { useState } from 'react'
import { Box, Flex, Grid, Input, Spinner } from '@chakra-ui/react'
import { LuSearch as Search, LuCheck as Check, LuUserPlus as UserPlus, LuShieldCheck as ShieldCheck } from 'react-icons/lu'
import { useFormColors, FormLabel, DatePicker } from '@minetrace/ui/forms'
import { Modal, toaster, Text, useT } from '@minetrace/ui'
import { personsService, proveedoresService } from '../../../api/services'
import type { PersonResponse } from '../../../api/models/response/person'

/** Datos resueltos de un manipulador, listos para enviar al backend. */
export interface ManipuladorLocal {
  personId: string
  fullName: string
  identityDoc: string
  licenseNumber?: string
  expirationDate?: string
  sucamecValidated: boolean
}

interface ManipuladorModalProps {
  open: boolean
  onClose: () => void
  onSave: (m: ManipuladorLocal) => Promise<void> | void
  /** PersonIds ya registrados como manipuladores en este Derecho Minero (no-duplicados). */
  existingPersonIds?: string[]
}

const EMPTY_NEW = { names: '', paternalSurname: '', maternalSurname: '' }

export default function ManipuladorModal({ open, onClose, onSave, existingPersonIds = [] }: ManipuladorModalProps) {
  const c = useFormColors()
  const { t } = useT()

  const [searchDoc, setSearchDoc] = useState('')
  const [foundPerson, setFoundPerson] = useState<PersonResponse | null>(null)
  const [searching, setSearching] = useState(false)
  const [saving, setSaving] = useState(false)

  const [searchedDoc, setSearchedDoc] = useState('')
  const [creating, setCreating] = useState(false)
  const [creatingPerson, setCreatingPerson] = useState(false)
  const [newPerson, setNewPerson] = useState(EMPTY_NEW)

  const [picked, setPicked] = useState<{ personId: string; fullName: string; identityDoc: string } | null>(null)

  // Datos de licencia SUCAMEC.
  const [licenseNumber, setLicenseNumber] = useState('')
  const [expirationDate, setExpirationDate] = useState('')
  const [sucamecValidated, setSucamecValidated] = useState(false)
  const [sucamecStatus, setSucamecStatus] = useState<string | null>(null)

  const inputStyles = {
    bg: c.bgInput,
    border: 'none',
    borderRadius: 'xl',
    color: c.text,
    _placeholder: { color: c.textMuted },
    _focus: { outline: 'none', ring: '1px', ringColor: c.accent },
  } as const
  // Campos DENTRO de los recuadros grises (persona nueva, licencia): con bgInput sobre
  // bgHover (#ebefed sobre #e6e9e7) el input era invisible y no se sabía dónde escribir.
  const boxedInputStyles = { ...inputStyles, bg: c.bgCard, border: '1px solid', borderColor: c.border } as const

  const enterCreateMode = (doc: string, prefill: typeof EMPTY_NEW) => {
    setSearchedDoc(doc)
    setNewPerson(prefill)
    setCreating(true)
  }

  // Aplica el resultado de SUCAMEC (best-effort) a los campos de licencia.
  const applySucamec = (dni: string) => {
    proveedoresService.consultarSucamecManipulador(dni).then((s) => {
      if (!s?.found) return
      if (s.licenseNumber) setLicenseNumber(s.licenseNumber)
      if (s.expirationDate) setExpirationDate(s.expirationDate)
      setSucamecValidated(true)
      setSucamecStatus(s.status)
      toaster.success({ description: t('proveedores.wizard.step7.sucamecFound') })
    }).catch(() => { /* silencioso: queda registro manual */ })
  }

  const handleSearch = async () => {
    const doc = searchDoc.trim()
    if (!doc) return
    setFoundPerson(null)
    setCreating(false)
    setSearching(true)
    try {
      // Combinado: datos de persona (RENIEC/SUNAT) + licencia (SUCAMEC) en paralelo.
      if (doc.length === 8 && /^\d+$/.test(doc)) {
        applySucamec(doc)
        const lookup = await personsService.lookupDni(doc)
        if (lookup.foundInDatabase && lookup.personId) {
          const full = await personsService.getById(lookup.personId)
          setFoundPerson(full)
        } else if (lookup.source === 'sunat') {
          enterCreateMode(doc, {
            names: lookup.names ?? '',
            paternalSurname: lookup.paternalSurname ?? '',
            maternalSurname: lookup.maternalSurname ?? '',
          })
        } else {
          enterCreateMode(doc, EMPTY_NEW)
        }
      } else {
        const data = await personsService.searchByDoc(doc)
        if (data) setFoundPerson(data)
        else enterCreateMode(doc, EMPTY_NEW)
      }
    } catch {
      enterCreateMode(doc, EMPTY_NEW)
    } finally {
      setSearching(false)
    }
  }

  const handlePick = () => {
    if (!foundPerson) return
    // Defensa: no permitir elegir a una persona ya registrada en este Derecho Minero.
    if (existingPersonIds.includes(foundPerson.id)) {
      toaster.create({ type: 'warning', description: t('proveedores.wizard.step7.handlerDuplicate') })
      return
    }
    setPicked({ personId: foundPerson.id, fullName: foundPerson.fullName, identityDoc: foundPerson.identityDoc })
    setFoundPerson(null)
  }

  const handleCreatePerson = async () => {
    if (!newPerson.names.trim() || !newPerson.paternalSurname.trim()) {
      toaster.error({ description: t('proveedores.wizard.step7.nameRequired') })
      return
    }
    setCreatingPerson(true)
    try {
      const documentType = searchedDoc.length === 8 && /^\d+$/.test(searchedDoc) ? 1 : 2
      const person = await personsService.createStep1({
        documentType,
        identityDoc: searchedDoc,
        names: newPerson.names.trim(),
        paternalSurname: newPerson.paternalSurname.trim(),
        maternalSurname: newPerson.maternalSurname.trim() || undefined,
      })
      setPicked({ personId: person.id, fullName: person.fullName, identityDoc: person.identityDoc })
      setCreating(false)
    } catch (e) {
      toaster.error({ description: e instanceof Error && e.message ? e.message : t('proveedores.wizard.step7.createError') })
    } finally {
      setCreatingPerson(false)
    }
  }

  const handleSave = async () => {
    if (!picked) {
      toaster.error({ description: t('proveedores.wizard.step7.pickRequired') })
      return
    }
    setSaving(true)
    try {
      await onSave({
        personId: picked.personId,
        fullName: picked.fullName,
        identityDoc: picked.identityDoc,
        licenseNumber: licenseNumber.trim() || undefined,
        expirationDate: expirationDate || undefined,
        sucamecValidated,
      })
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('proveedores.wizard.step7.handlerModalTitle')}
      size="lg"
      footer={
        <Flex gap={3} justifyContent="flex-end">
          <Box as="button" type="button" px={4} py={2} borderRadius="lg" border="1px solid" borderColor={c.border} color={c.text} fontWeight="600" fontSize="sm" cursor="pointer" _hover={{ bg: c.bgHover }} onClick={onClose}>
            {t('common.cancel')}
          </Box>
          <Box as="button" type="button" px={4} py={2} borderRadius="lg" bg={c.accent} color="white" fontWeight="600" fontSize="sm" cursor="pointer" opacity={picked && !saving ? 1 : 0.5} _hover={{ opacity: picked && !saving ? 0.9 : 0.5 }} onClick={handleSave}>
            {saving ? <Spinner size="sm" color="white" /> : t('common.save')}
          </Box>
        </Flex>
      }
    >
      {/* Búsqueda de persona por DNI */}
      {!picked && (
        <Box mb={4}>
          <FormLabel>{t('proveedores.wizard.step7.handlerDni')}</FormLabel>
          <Flex gap={2}>
            <Input
              value={searchDoc}
              // DNI (8 dígitos) o CE (9-12 alfanuméricos): filtrar en vivo evita que se
              // busque con espacios o guiones pegados y el lookup falle sin motivo visible.
              onChange={(e) => setSearchDoc(e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 12))}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch() } }}
              placeholder={t('proveedores.wizard.step7.handlerDniPlaceholder')}
              maxLength={12}
              {...inputStyles}
            />
            <Box as="button" type="button" display="flex" alignItems="center" justifyContent="center" px={4} bg={c.accentGradient} color="white" borderRadius="xl" cursor="pointer" flexShrink={0} minW="48px" opacity={!searchDoc.trim() || searching ? 0.5 : 1} onClick={handleSearch}>
              {searching ? <Spinner size="sm" color="white" /> : <Search size={18} />}
            </Box>
          </Flex>
          <Text fontSize="xs" color={c.textMuted} mt={2}>{t('proveedores.wizard.step7.handlerSearchHint')}</Text>

          {foundPerson && (() => {
            const isDup = existingPersonIds.includes(foundPerson.id)
            return (
              <Box bg={isDup ? 'rgba(212, 154, 42, 0.10)' : `${c.accent}10`} border="1px solid" borderColor={isDup ? 'rgba(212, 154, 42, 0.4)' : `${c.accent}30`} borderRadius="xl" p={3} mt={3}>
                <Flex justifyContent="space-between" alignItems="center" gap={3}>
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color={c.text}>{foundPerson.fullName}</Text>
                    <Text fontSize="xs" color={c.textMuted}>{foundPerson.identityDoc}</Text>
                  </Box>
                  {isDup ? (
                    <Text fontSize="xs" fontWeight="600" color="#8a5f0b" flexShrink={0} textAlign="right">
                      {t('proveedores.wizard.step7.handlerDuplicate')}
                    </Text>
                  ) : (
                    <Box as="button" type="button" display="flex" alignItems="center" gap={2} px={3} py={1.5} bg={c.accent} color="white" borderRadius="lg" fontSize="xs" fontWeight="600" cursor="pointer" flexShrink={0} onClick={handlePick}>
                      <Check size={14} />{t('proveedores.wizard.step7.handlerSelect')}
                    </Box>
                  )}
                </Flex>
              </Box>
            )
          })()}

          {creating && (
            <Box bg={c.bgHover} borderRadius="xl" p={4} mt={3}>
              <Text fontSize="sm" fontWeight="600" color={c.text}>{t('proveedores.wizard.step7.handlerCreateTitle')}</Text>
              <Text fontSize="xs" color={c.textMuted} mb={3}>{t('proveedores.wizard.step7.handlerCreateHint')}</Text>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={3}>
                <Box>
                  <FormLabel required>{t('proveedores.wizard.step7.handlerNames')}</FormLabel>
                  <Input value={newPerson.names} onChange={(e) => setNewPerson((p) => ({ ...p, names: e.target.value }))} {...boxedInputStyles} />
                </Box>
                <Box>
                  <FormLabel required>{t('proveedores.wizard.step7.handlerPaternal')}</FormLabel>
                  <Input value={newPerson.paternalSurname} onChange={(e) => setNewPerson((p) => ({ ...p, paternalSurname: e.target.value }))} {...boxedInputStyles} />
                </Box>
                <Box>
                  <FormLabel>{t('proveedores.wizard.step7.handlerMaternal')}</FormLabel>
                  <Input value={newPerson.maternalSurname} onChange={(e) => setNewPerson((p) => ({ ...p, maternalSurname: e.target.value }))} {...boxedInputStyles} />
                </Box>
              </Grid>
              <Flex justifyContent="flex-end" mt={3}>
                <Box as="button" type="button" display="flex" alignItems="center" gap={2} px={4} py={2} bg={c.accent} color="white" borderRadius="lg" fontSize="sm" fontWeight="600" cursor="pointer" opacity={creatingPerson ? 0.6 : 1} _hover={{ opacity: 0.9 }} onClick={handleCreatePerson}>
                  {creatingPerson ? <Spinner size="sm" color="white" /> : <UserPlus size={14} />}
                  {t('proveedores.wizard.step7.handlerCreateAndUse')}
                </Box>
              </Flex>
            </Box>
          )}
        </Box>
      )}

      {/* Persona elegida + datos de licencia SUCAMEC */}
      {picked && (
        <>
          <Box bg={c.bgHover} borderRadius="xl" p={3} mb={4}>
            <Flex justifyContent="space-between" alignItems="center">
              <Box>
                <Text fontSize="sm" fontWeight="600" color={c.text}>{picked.fullName}</Text>
                <Text fontSize="xs" color={c.textMuted}>{picked.identityDoc}</Text>
              </Box>
              {sucamecValidated && (
                <Flex alignItems="center" gap={1} px={2} py={1} bg={`${c.accent}15`} borderRadius="md">
                  <ShieldCheck size={14} color={c.accent} />
                  <Text fontSize="xs" fontWeight="600" color={c.accent}>{t('proveedores.wizard.step7.sucamecValidated')}</Text>
                </Flex>
              )}
            </Flex>
          </Box>

          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
            <Box>
              <FormLabel>{t('proveedores.wizard.step7.handlerLicense')}</FormLabel>
              <Input value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value.slice(0, 50))} placeholder={t('proveedores.wizard.step7.handlerLicensePlaceholder')} {...boxedInputStyles} />
            </Box>
            <Box>
              <FormLabel>{t('proveedores.wizard.step7.handlerExpiration')}</FormLabel>
              <DatePicker value={expirationDate} onChange={setExpirationDate} />
            </Box>
          </Grid>
          {sucamecStatus && sucamecStatus !== 'unknown' && (
            <Text fontSize="xs" color={c.textMuted} mt={2}>
              {t('proveedores.wizard.step7.sucamecStatusInfo', { status: t(`proveedores.wizard.step7.status.${sucamecStatus}`) })}
            </Text>
          )}
        </>
      )}
    </Modal>
  )
}
